import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  FileCheck,
  ShieldCheck,
  Users,
  Bell,
  ArrowRight,
  ClipboardList,
  PenTool,
  Gauge,
  CheckCircle2,
  Stethoscope,
  Lock,
  Clock,
  Play,
  UserCheck,
  Microscope,
  Building2,
  Sparkles,
  TrendingUp,
  FileSignature,
  Activity,
  ScrollText,
  ChevronRight,
  Crown,
  CalendarDays,
  MessageCircle,
  Rocket,
} from "lucide-react";

// iter164h — GooeyNav replaces the desktop horizontal links, GradualBlur
// fades the bottom of the hero into the page below, FluidGlass is shelved
// (requires user-supplied .glb assets), Folder removed from the Why section,
// CardSwap enlarged and click-to-swap enabled.
import ShinyText from "../components/reactbits/ShinyText";
import MagicBento from "../components/reactbits/MagicBento";
import Magnet from "../components/reactbits/Magnet";
import Counter from "../components/reactbits/Counter";
import AnimatedContent from "../components/reactbits/AnimatedContent";
import CardSwap, { Card as SwapCard } from "../components/reactbits/CardSwap";
import StaggeredMenu from "../components/reactbits/StaggeredMenu";
import GradualBlur from "../components/reactbits/GradualBlur";
import ScrollStack, { ScrollStackItem } from "../components/reactbits/ScrollStack";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1719496525117-469ab037bfe0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxjbGluaWNhbCUyMHJlc2VhcmNofGVufDB8fHxibHVlfDE3Nzc5MjE2OTV8MA&ixlib=rb-4.1.0&q=85&w=1600";

// Local MP4 (in public/landing). Self-hosted to keep the experience embedded — no third-party
// chrome, no popouts, no external links. The poster is a real frame ripped from the video so
// the still-state shows actual product UI, not a black rectangle.
const DEMO_VIDEO_SRC = "/landing/demo.mp4";
const DEMO_POSTER = "/landing/demo-poster.jpg";

function FadeInOnScroll({ children, delay = 0, className = "" }) {
  const ref = React.useRef(null);
  const [visible, setVisible] = useState(false);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -10% 0px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{ transitionDelay: visible ? `${delay}ms` : "0ms" }}
      className={[
        "transition-all duration-700 ease-out will-change-transform",
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6",
        className,
      ].join(" ")}
    >
      {children}
    </div>
  );
}

function DemoVideoSection() {
  const videoRef = React.useRef(null);
  const [playing, setPlaying] = useState(false);

  const handlePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    v.play().catch(() => {/* user gesture should satisfy autoplay; ignore reject */});
  };

  return (
    <section id="product-tour" className="py-24 lg:py-32 bg-gradient-to-b from-white via-slate-50 to-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeInOnScroll>
          <div className="max-w-3xl mx-auto text-center">
            <div className="text-sm font-semibold text-[#1e3a5f] uppercase tracking-wider">
              See it in action
            </div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">
              A 2-minute tour of ClinicalFlow
            </h2>
            <p className="mt-4 text-base sm:text-lg text-slate-600">
              Watch how research offices move FEAs, CTAs, IRBs, CDAs, Financial Transaction and more.. reviews and e-signatures through one
              audit-ready workflow — no spreadsheets, no email chains.
            </p>
          </div>
        </FadeInOnScroll>

        {/* Bordered card wrapper matching the hero card styling — soft outer shadow,
            inner ring + inset highlight that ties the section to the rest of the page. */}
        <FadeInOnScroll delay={150}>
          <div className="mt-14 mx-auto max-w-5xl">
            <div className="relative rounded-3xl p-2 bg-white shadow-[0_25px_70px_-15px_rgba(30,58,95,0.25)] ring-1 ring-slate-200">
              <div
                className="relative w-full rounded-2xl overflow-hidden ring-1 ring-slate-200/70 shadow-inner bg-slate-900"
                style={{ aspectRatio: "16 / 9" }}
                data-testid="demo-video-frame"
              >
                <video
                  ref={videoRef}
                  src={DEMO_VIDEO_SRC}
                  poster={DEMO_POSTER}
                  preload="metadata"
                  playsInline
                  controls={playing}
                  controlsList="nodownload noplaybackrate noremoteplayback"
                  disablePictureInPicture
                  onContextMenu={(e) => e.preventDefault()}
                  onPlay={() => setPlaying(true)}
                  onPause={() => setPlaying(false)}
                  onEnded={() => setPlaying(false)}
                  className="absolute inset-0 w-full h-full bg-black"
                  data-testid="demo-video-el"
                />
                {/* Custom play overlay — sits on top of the still first frame until the user clicks.
                    Hides as soon as playback starts. */}
                {!playing && (
                  <button
                    type="button"
                    onClick={handlePlay}
                    className="group absolute inset-0 w-full h-full flex items-center justify-center cursor-pointer focus:outline-none"
                    aria-label="Play product demo video"
                    data-testid="demo-video-play-btn"
                  >
                    <span className="absolute inset-0 bg-black/25 group-hover:bg-black/35 transition-colors" />
                    <span className="relative z-10 flex items-center justify-center w-20 h-20 rounded-full bg-white/95 shadow-2xl group-hover:scale-110 transition-transform">
                      <Play className="w-8 h-8 text-[#1e3a5f] ml-1" fill="currentColor" />
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </FadeInOnScroll>
      </div>
    </section>
  );
}

const features = [
  {
    icon: ClipboardList,
    title: "Unified FEA & CTA workflows",
    desc: "Run feasibility assessments and clinical trial agreements on one platform with shared context, custom fields, and document management.",
  },
  {
    icon: Users,
    title: "Role-based dashboards",
    desc: "Purpose-built views for CRDs, chairmen, directors, coordinators, investigators, reviewers, and sponsor reps — each sees exactly what they need.",
  },
  {
    icon: PenTool,
    title: "E-signatures & audit trail",
    desc: "Legally binding signatures with typed or drawn captures, automated PDF generation, and an immutable chronological audit log for every action.",
  },
  {
    icon: Bell,
    title: "Real-time collaboration",
    desc: "Threaded discussions, @mentions, live notifications over WebSockets, and configurable email templates per stage and role.",
  },
  {
    icon: ShieldCheck,
    title: "Enterprise-grade security",
    desc: "JWT auth, granular role permissions, encrypted credentials, CSRF protection, and full compliance-ready audit trails.",
  },
  {
    icon: Gauge,
    title: "Built-in scheduler",
    desc: "Automated reminders and escalations based on stage-specific policies so reviews, approvals, and signatures never fall through the cracks.",
  },
];

const steps = [
  {
    num: "01",
    title: "Submit request",
    desc: "Sponsor reps submit FEA or CTA requests with custom fields, supporting documents, and target specialties.",
    icon: ClipboardList,
    accent: "from-cyan-300 to-sky-400",
    highlights: ["Custom fields", "Document upload", "Target specialties"],
  },
  {
    num: "02",
    title: "Route & review",
    desc: "Requests automatically route to the right reviewers based on role, specialty, and workflow stage.",
    icon: Users,
    accent: "from-violet-300 to-fuchsia-400",
    highlights: ["Role-aware routing", "Specialty matching", "Stage-based logic"],
  },
  {
    num: "03",
    title: "Collaborate & sign",
    desc: "Stakeholders discuss, upload documents, and capture e-signatures — all with a complete audit trail.",
    icon: PenTool,
    accent: "from-emerald-300 to-teal-400",
    highlights: ["Threaded discussions", "Granular e-signatures", "Immutable audit"],
  },
  {
    num: "04",
    title: "Approve & archive",
    desc: "Final approval triggers PDF generation, status updates, and automatic archival for compliance.",
    icon: ShieldCheck,
    accent: "from-amber-300 to-orange-400",
    highlights: ["PDF generation", "QR verification", "Auto-archival"],
  },
];

// iter164h — Stats refreshed to real business metrics. Counter still animates
// the count-up; `prefix=""` and `suffix="+"` give us the recognisable "200+"
// look. Order matches what marketing wants up front (consultants → specialists
// → pharma logos → active users).
// iter164i — Each tile now carries an icon, accent color, and short caption
// so the new dark-navy "Trusted by clinical research" strip below the hero
// reads as a proper social-proof section, not a row of bare numbers.
const stats = [
  {
    value: 100, suffix: "+",
    label: "Consultants onboarded",
    caption: "Across leading research centers",
    icon: UserCheck,
    accent: "from-cyan-300 to-sky-400",
  },
  {
    value: 30,  suffix: "+",
    label: "Medical specialties represented",
    caption: "Every specialty & sub-specialty — covered",
    icon: Microscope,
    accent: "from-violet-300 to-fuchsia-400",
  },
  {
    value: 15,  suffix: "+",
    label: "Leading global pharma companies",
    caption: "Active sponsor partnerships",
    icon: Building2,
    accent: "from-amber-300 to-orange-400",
  },
  {
    value: 200, suffix: "+",
    label: "Active users",
    caption: "Logging in every week",
    icon: TrendingUp,
    accent: "from-emerald-300 to-teal-400",
  },
];

// iter164f — MagicBento card schema derived from the existing `features`
// array so the page stays the single source of truth for product copy.
// iter165 — Card surface deepened from `#1e3a5f` to a true navy `#0a1f3d`
// (closer to ClinicalFlow's brand navy ink) — was reading too "blue" before.
const featuresAsBentoCards = [
  { color: '#0a1f3d', label: 'Workflows',  title: 'Unified FEA & CTA',     description: features[0].desc },
  { color: '#0a1f3d', label: 'Roles',      title: 'Role-based dashboards', description: features[1].desc },
  { color: '#0a1f3d', label: 'Signatures', title: 'E-signatures & audit',  description: features[2].desc },
  { color: '#0a1f3d', label: 'Collab',     title: 'Real-time collaboration', description: features[3].desc },
  { color: '#0a1f3d', label: 'Security',   title: 'Enterprise security',   description: features[4].desc },
  { color: '#0a1f3d', label: 'Automation', title: 'Built-in scheduler',    description: features[5].desc },
];

// iter165h — Slide-in panel content for the + Menu (StaggeredMenu's
// `renderPanel`). Combines a persona picker ("I am a…") with universal
// Get-Started CTAs so visitors can self-segment AND convert in one step.
// PERSONAS:  PI · CRD/Admin · Sponsor Rep · Chairman/Reviewer
// CTAS:      Start free trial · Watch demo · Book a call · Talk to us
const personaTiles = [
  {
    key: 'pi',
    title: 'Principal Investigator',
    desc: 'Pick feasibility & review work that fits your specialty.',
    icon: Stethoscope,
    accent: 'from-cyan-300 to-sky-400',
    to: '/register?as=investigator',
  },
  {
    key: 'crd',
    title: 'CRD / Hospital admin',
    desc: 'Route requests, manage reviewers, own the audit trail.',
    icon: ShieldCheck,
    accent: 'from-violet-300 to-fuchsia-400',
    to: '/register',
  },
  {
    key: 'sponsor',
    title: 'Sponsor / CRO Rep',
    desc: 'Submit & track FEA / CTA / FT across every site.',
    icon: Building2,
    accent: 'from-amber-300 to-orange-400',
    to: '/register?as=company',
  },
  {
    key: 'chairman',
    title: 'Chairman / Reviewer',
    desc: 'Configure signatories, approve, sign — all in one place.',
    icon: Crown,
    accent: 'from-emerald-300 to-teal-400',
    to: '/login',
  },
];

function GetStartedPanel({ onClose }) {
  return (
    <div className="relative h-full flex flex-col" data-testid="get-started-panel">
      {/* iter167 Issue 32 — Decorative overlays removed entirely per user
          spec. Previous attempts (smaller glows, overflow-hidden) still
          produced a faint halo on the panel edge because CSS `filter:
          blur()` paints outside its containing block. The panel now ships
          flat — solid navy background, single 1px ring on each tile.
          If we want depth later, add it via per-tile hover glows, not a
          panel-wide overlay. */}

      <div className="relative flex-1 flex flex-col gap-7">
        {/* Header — small uppercase mono label */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-cyan-200/80">
            I am a…
          </div>
          <h2 className="mt-2 text-2xl font-semibold tracking-tight text-white">
            Get started in seconds
          </h2>
          <p className="mt-1 text-[13px] text-blue-100/70 leading-relaxed">
            Pick the role that best describes you and we&apos;ll tailor the next step.
          </p>
        </div>

        {/* Persona tiles — 2x2 grid */}
        <div className="grid grid-cols-2 gap-3">
          {personaTiles.map((p) => {
            const Icon = p.icon;
            return (
              <Link
                key={p.key}
                to={p.to}
                onClick={onClose}
                data-testid={`persona-${p.key}`}
                className="group relative rounded-2xl bg-white/[0.05] hover:bg-white/[0.09] ring-1 ring-white/10 hover:ring-white/25 p-4 transition-all"
              >
                <div className={`h-9 w-9 rounded-xl bg-gradient-to-br ${p.accent} p-[1.5px] shadow-lg shadow-black/30`}>
                  <div className="h-full w-full rounded-[10px] bg-[#06122a]/85 flex items-center justify-center">
                    <Icon className="h-4 w-4 text-white" strokeWidth={1.9} />
                  </div>
                </div>
                <div className="mt-3 text-[13px] font-semibold text-white leading-tight">
                  {p.title}
                </div>
                <div className="mt-1 text-[11px] text-blue-100/70 leading-snug">
                  {p.desc}
                </div>
                <ChevronRight className="absolute top-4 right-4 h-3.5 w-3.5 text-blue-200/40 group-hover:text-cyan-200 group-hover:translate-x-0.5 transition-all" />
              </Link>
            );
          })}
        </div>

        {/* Divider */}
        <div className="flex items-center gap-3">
          <div className="h-px flex-1 bg-white/10" />
          <span className="text-[10px] uppercase tracking-[0.22em] font-mono text-blue-100/60">
            Quick actions
          </span>
          <div className="h-px flex-1 bg-white/10" />
        </div>

        {/* CTAs */}
        <div className="grid grid-cols-1 gap-2.5">
          {/* Primary — Start free trial */}
          <Link
            to="/register"
            onClick={onClose}
            data-testid="menu-cta-trial"
            className="group flex items-center justify-between gap-3 rounded-2xl bg-gradient-to-r from-cyan-400 to-sky-500 hover:from-cyan-300 hover:to-sky-400 text-[#06122a] px-4 py-3.5 font-semibold transition-all"
          >
            <span className="flex items-center gap-3">
              <Rocket className="h-4 w-4" />
              Start free trial
            </span>
            <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
          </Link>

          {/* Secondary trio */}
          {[
            { icon: Play, label: 'Watch 2-min demo', sub: 'See the platform in action', href: '#product-tour', testid: 'menu-cta-demo' },
            { icon: CalendarDays, label: 'Book a call', sub: '30-min walkthrough', href: 'mailto:sales@clinicalflow.com?subject=Book%20a%20call', testid: 'menu-cta-call' },
            { icon: MessageCircle, label: 'Talk to us', sub: 'Reply within 1 business day', href: 'mailto:Contact@aretion.org', testid: 'menu-cta-talk' },
          ].map(({ icon: Icon, label, sub, href, testid }) => (
            <a
              key={label}
              href={href}
              onClick={onClose}
              data-testid={testid}
              className="group flex items-center justify-between gap-3 rounded-2xl bg-white/[0.05] hover:bg-white/[0.09] ring-1 ring-white/10 hover:ring-white/25 px-4 py-3 transition-all"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-9 w-9 rounded-xl bg-white/[0.07] ring-1 ring-white/15 flex items-center justify-center shrink-0">
                  <Icon className="h-4 w-4 text-cyan-200" />
                </div>
                <div className="min-w-0">
                  <div className="text-[13px] font-semibold text-white truncate">{label}</div>
                  <div className="text-[11px] text-blue-100/65 truncate">{sub}</div>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-blue-200/50 group-hover:text-cyan-200 group-hover:translate-x-0.5 transition-all shrink-0" />
            </a>
          ))}
        </div>
      </div>

      {/* Footer — Sign in link + compliance pills */}
      <div className="relative mt-7 pt-5 border-t border-white/10">
        <div className="flex items-center justify-between gap-3">
          <Link
            to="/login"
            onClick={onClose}
            data-testid="menu-signin-link"
            className="inline-flex items-center gap-1.5 text-sm text-white/90 hover:text-cyan-200 font-medium transition-colors"
          >
            Already have an account?
            <span className="underline underline-offset-2">Sign in</span>
          </Link>
          <div className="hidden sm:flex items-center gap-3 text-[10px] uppercase tracking-wider text-blue-100/55">
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="h-3 w-3 text-emerald-300" />
              21 CFR Part 11
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Lock className="h-3 w-3 text-cyan-200" />
              Encrypted
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LandingPage() {
  // iter165 — Track which CardSwap card is at the front so its KPI counters
  // re-animate from zero each time it lands. Index matches the order of
  // children passed to <CardSwap />: 0 = CTA, 1 = FEA, 2 = IRB.
  const [frontCardIndex, setFrontCardIndex] = useState(0);

  // iter164g — StaggeredMenu config. Items mirror the desktop nav so visitors
  // who open the menu get one consistent IA. socialItems are placeholder links
  // teams can swap when external profiles go live.
  const menuItems = [
    { label: 'Features', link: '#features', ariaLabel: 'Jump to features section' },
    { label: 'How it works', link: '#how-it-works', ariaLabel: 'Jump to how it works section' },
    { label: 'Product tour', link: '#product-tour', ariaLabel: 'Jump to product tour' },
    { label: 'Why us', link: '#why', ariaLabel: 'Jump to why ClinicalFlow' },
    { label: 'FAQ', link: '#faq', ariaLabel: 'Jump to FAQ' },
    { label: 'Sign in', link: '/login', ariaLabel: 'Go to sign-in page' },
  ];
  const menuSocials = [
    { label: 'Email support', link: 'mailto:support@clinicalflow.com' },
    { label: 'GitHub', link: 'https://github.com' },
    { label: 'LinkedIn', link: 'https://linkedin.com' },
  ];

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* iter164g — StaggeredMenu (ReactBits). Fixed in the top-right of the
          viewport so it floats above the existing horizontal nav for a bold
          full-screen overlay nav experience. The desktop horizontal links
          stay for accessibility / fast jumps. */}
      {/* iter165h — Custom panel: + Menu now opens a slide-in with a
          "I am a…" persona picker (4 tiles) and 4 universal "Get started"
          CTAs (Start trial / Watch demo / Book a call / Talk to us). The
          StaggeredMenu shell still owns the open/close GSAP animations via
          its `renderPanel` render prop. */}
      <StaggeredMenu
        position="right"
        colors={['#1e3a5f', '#0f1e34']}
        items={menuItems}
        socialItems={menuSocials}
        displaySocials={false}
        displayItemNumbering={false}
        logoUrl="/favicon-192.png"
        menuButtonColor="#1e3a5f"
        openMenuButtonColor="#ffffff"
        accentColor="#B497CF"
        isFixed
        closeOnClickAway
        renderPanel={({ close }) => <GetStartedPanel onClose={close} />}
      />
      {/* ====== NAV ====== */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img
              src="/favicon-192.png"
              alt="ClinicalFlow"
              className="h-8 w-8 rounded-md"
            />
            <span className="font-semibold text-slate-900 tracking-tight">
              ClinicalFlow
            </span>
          </Link>

          {/* iter164j — GooeyNav removed per user; restored the plain inline
              horizontal nav for a calmer, more medical feel. */}
          <nav className="hidden md:flex items-center gap-7 text-sm">
            <a href="#features"      className="text-slate-600 hover:text-slate-900 transition-colors font-medium">Features</a>
            <a href="#how-it-works"  className="text-slate-600 hover:text-slate-900 transition-colors font-medium">How it works</a>
            <a href="#product-tour"  className="text-slate-600 hover:text-slate-900 transition-colors font-medium" data-testid="nav-product-tour">Product tour</a>
            <a href="#why"           className="text-slate-600 hover:text-slate-900 transition-colors font-medium">Why</a>
            <a href="#faq"           className="text-slate-600 hover:text-slate-900 transition-colors font-medium">FAQ</a>
          </nav>

          {/* iter164g — Sign-in button hidden on the header right since the
              StaggeredMenu toggle sits there now. The Sign In CTA still lives
              in the hero, the menu panel, and the final CTA section. */}
          <div className="flex items-center gap-3 invisible">
            <Link to="/login">
              <Button className="bg-[#1e3a5f] hover:bg-[#152c4a] text-white shadow-sm">
                Sign in
                <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* ====== HERO ====== */}
      <section className="relative overflow-hidden">
        {/* iter164f — Grainient background removed per user. Restored the
            original subtle decorative blue/indigo blobs that read as clean
            and medical rather than synthwave. */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-24 -left-20 w-[500px] h-[500px] bg-blue-100/50 rounded-full blur-3xl" />
          <div className="absolute top-56 right-0 w-[400px] h-[400px] bg-indigo-100/40 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28">
          {/* iter165e — Widened the column gap from gap-12 → gap-x-20 so the
              CardSwap visual breathes away from the copy column, and the
              left column now uses its own vertical rhythm (mb-* / mt-*) for
              a calmer, less crowded hero. */}
          <div className="grid lg:grid-cols-12 lg:gap-x-20 gap-y-12 items-center">
            {/* Left: copy */}
            <div className="lg:col-span-7">
              {/* iter165b — Big ClinicalFlow brand mark to fill the empty
                  vertical space above the hero headline. Logo glyph now has
                  NO background (transparent) per user; the chip that used to
                  sit below ("Clinical Research Management System") has been
                  promoted to live INSIDE the brand block as the wordmark's
                  subline, replacing the previous mono tagline. */}
              <AnimatedContent>
                <div className="flex items-center gap-5 mb-12" data-testid="hero-brand-mark">
                  <img
                    src="/favicon-192.png"
                    alt="ClinicalFlow logo"
                    className="h-16 w-16 sm:h-20 sm:w-20 object-contain drop-shadow-sm shrink-0"
                  />
                  <div className="leading-none">
                    <div className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">
                      ClinicalFlow
                    </div>
                    <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 text-blue-800 text-xs font-medium border border-blue-100">
                      <Stethoscope className="h-3.5 w-3.5" />
                      Clinical Research Management System
                    </div>
                  </div>
                </div>
              </AnimatedContent>

              <AnimatedContent delay={80}>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900 leading-[1.1]">
                  One workflow. From{" "}
                  <ShinyText text="first feasibility" speed={6} />
                  {" "}to{" "}
                  <ShinyText text="signed contract." speed={6} />
                </h1>
              </AnimatedContent>

              <AnimatedContent delay={160}>
                <p className="mt-8 text-lg text-slate-600 max-w-2xl leading-relaxed">
                  ClinicalFlow unifies FEAs, CTAs, IRBs, CDAs, Financial
                  Transactions, reviews and e-signatures into a single
                  audit-ready trail — no spreadsheets, no email chains, no
                  version chaos.
                </p>
              </AnimatedContent>

              <AnimatedContent delay={240}>
                <div className="mt-10 flex flex-col sm:flex-row gap-3">
                  <Magnet padding={60} magnetStrength={6}>
                    <Link to="/login">
                      <Button
                        size="lg"
                        className="bg-[#1e3a5f] hover:bg-[#152c4a] text-white shadow-lg shadow-[#1e3a5f]/20 px-6 h-12 text-base w-full sm:w-auto"
                        data-testid="hero-demo-btn"
                      >
                        Sign in
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </Magnet>
                  <Magnet padding={60} magnetStrength={8}>
                    <a href="#features">
                      <Button
                        variant="outline"
                        size="lg"
                        className="border-slate-300 hover:bg-slate-50 px-6 h-12 text-base w-full sm:w-auto"
                      >
                        Explore features
                      </Button>
                    </a>
                  </Magnet>
                  <Magnet padding={60} magnetStrength={8}>
                    <a href="#product-tour">
                      <Button
                        variant="ghost"
                        size="lg"
                        className="text-[#1e3a5f] hover:bg-slate-100 px-6 h-12 text-base w-full sm:w-auto"
                        data-testid="hero-product-tour-btn"
                      >
                        <Play className="mr-2 h-4 w-4" fill="currentColor" />
                        Watch product tour
                      </Button>
                    </a>
                  </Magnet>
                </div>
              </AnimatedContent>

              {/* Trust bar */}
              <AnimatedContent delay={320}>
                <div className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-slate-500">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    Customizable workflows
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    E-signatures built-in
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    Immutable audit trail
                  </div>
                </div>
              </AnimatedContent>
            </div>

            {/* Right: hero visual — iter164j: enlarged CardSwap (560×420 in a
                640px shell) with three vivid, content-rich cards (CTA, FEA,
                IRB). Click-to-drop now targets the card the user actually
                clicked. The "Audit-ready" + "Automated reminders" floating
                cards were removed for a cleaner stage. */}
            <AnimatedContent delay={180} direction="horizontal" reverse className="lg:col-span-5 relative">
              <div className="relative h-[640px] flex items-center justify-center" data-testid="hero-card-swap">
                <CardSwap
                  width={560}
                  height={420}
                  cardDistance={70}
                  verticalDistance={84}
                  delay={5200}
                  pauseOnHover
                  skewAmount={5}
                  easing="elastic"
                  onCardChange={(idx) => setFrontCardIndex(idx)}
                >
                  {/* iter165 — Card 1: CTA. Premium navy with progress rail,
                      signatory avatar stack, gradient accent and KPI tile. */}
                  <SwapCard customClass="!bg-[linear-gradient(135deg,_#0b1d3a_0%,_#13315c_45%,_#06122a_100%)] text-white relative">
                    {/* Subtle grid + glow */}
                    <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
                         style={{ backgroundImage:'linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)', backgroundSize:'22px 22px' }} />
                    <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-cyan-500/20 blur-3xl pointer-events-none" />
                    <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-cyan-400 via-sky-300 to-blue-400" />

                    <div className="relative h-full p-7 flex flex-col">
                      <div className="flex items-center justify-between">
                        <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-white/10 backdrop-blur ring-1 ring-white/20 text-[10px] uppercase tracking-[0.18em] font-semibold text-cyan-100">
                          <FileSignature className="h-3 w-3" />
                          Clinical Trial Agreement
                        </span>
                        <span className="text-[10px] font-mono text-cyan-200/80 bg-white/5 px-2 py-0.5 rounded">CTA-2026-000017</span>
                      </div>

                      <h3 className="mt-4 text-[22px] font-semibold leading-tight tracking-tight">Multi-site CAR-T Study Agreement</h3>
                      <p className="mt-1 text-[13px] text-blue-100/80">Bristol Therapeutics · Hematology · 12 sites</p>

                      {/* KPI strip */}
                      <div className="mt-4 grid grid-cols-3 gap-2">
                        <div className="rounded-lg bg-white/[0.06] ring-1 ring-white/10 px-2.5 py-2">
                          <div className="text-[9px] uppercase tracking-wider text-cyan-200/70">Progress</div>
                          <div className="text-lg font-bold text-white leading-tight">
                            <Counter to={67} duration={1300} replayKey={`cta-prog-${frontCardIndex === 0 ? 'on' : 'idle'}`} />
                            <span className="text-xs text-cyan-200/80">%</span>
                          </div>
                        </div>
                        <div className="rounded-lg bg-white/[0.06] ring-1 ring-white/10 px-2.5 py-2">
                          <div className="text-[9px] uppercase tracking-wider text-cyan-200/70">Signatures</div>
                          <div className="text-lg font-bold text-white leading-tight">
                            <Counter to={2} duration={1100} replayKey={`cta-sig-${frontCardIndex === 0 ? 'on' : 'idle'}`} />
                            <span className="text-xs text-cyan-200/80">/3</span>
                          </div>
                        </div>
                        <div className="rounded-lg bg-white/[0.06] ring-1 ring-white/10 px-2.5 py-2">
                          <div className="text-[9px] uppercase tracking-wider text-cyan-200/70">Cycle time</div>
                          <div className="text-lg font-bold text-white leading-tight">
                            <Counter to={9} duration={1200} replayKey={`cta-cyc-${frontCardIndex === 0 ? 'on' : 'idle'}`} />
                            <span className="text-xs text-cyan-200/80">d</span>
                          </div>
                        </div>
                      </div>

                      <div className="mt-auto space-y-2">
                        <div className="flex items-center justify-between text-[12px] bg-white/[0.07] rounded-lg px-3 py-2 backdrop-blur ring-1 ring-white/10">
                          <span className="flex items-center gap-2 text-blue-100">
                            <span className="h-5 w-5 rounded-full bg-amber-400/20 ring-1 ring-amber-300/40 flex items-center justify-center text-[9px] font-bold text-amber-200">DR</span>
                            Director signature
                          </span>
                          <span className="inline-flex items-center gap-1.5 text-amber-200 font-semibold">
                            <span className="h-1.5 w-1.5 rounded-full bg-amber-300 animate-pulse" />
                            Pending
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-[12px] bg-white/[0.07] rounded-lg px-3 py-2 backdrop-blur ring-1 ring-white/10">
                          <span className="flex items-center gap-2 text-blue-100">
                            <span className="h-5 w-5 rounded-full bg-emerald-400/20 ring-1 ring-emerald-300/40 flex items-center justify-center text-[9px] font-bold text-emerald-200">ED</span>
                            Executive Director
                          </span>
                          <span className="inline-flex items-center gap-1.5 text-emerald-300 font-semibold">
                            <CheckCircle2 className="h-3 w-3" />
                            Signed · 2d ago
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-[12px] bg-white/[0.07] rounded-lg px-3 py-2 backdrop-blur ring-1 ring-white/10">
                          <span className="flex items-center gap-2 text-blue-100">
                            <span className="h-5 w-5 rounded-full bg-emerald-400/20 ring-1 ring-emerald-300/40 flex items-center justify-center text-[9px] font-bold text-emerald-200">CH</span>
                            Chairman review
                          </span>
                          <span className="inline-flex items-center gap-1.5 text-emerald-300 font-semibold">
                            <CheckCircle2 className="h-3 w-3" />
                            Approved
                          </span>
                        </div>
                      </div>
                    </div>
                  </SwapCard>

                  {/* iter165 — Card 2: FEA. Indigo→violet glassy with patient
                      pool meter and live submission state. */}
                  <SwapCard customClass="!bg-[linear-gradient(135deg,_#1e3a8a_0%,_#3730a3_45%,_#4c1d95_100%)] text-white relative">
                    <div className="absolute inset-0 opacity-[0.07] pointer-events-none"
                         style={{ backgroundImage:'radial-gradient(circle at 1px 1px, rgba(255,255,255,.6) 1px, transparent 0)', backgroundSize:'18px 18px' }} />
                    <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-fuchsia-500/25 blur-3xl pointer-events-none" />
                    <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-violet-400 via-fuchsia-300 to-pink-400" />

                    <div className="relative h-full p-7 flex flex-col">
                      <div className="flex items-center justify-between">
                        <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-white/15 backdrop-blur ring-1 ring-white/20 text-[10px] uppercase tracking-[0.18em] font-semibold text-violet-100">
                          <Activity className="h-3 w-3" />
                          Feasibility Assessment
                        </span>
                        <span className="text-[10px] font-mono text-violet-100/90 bg-white/5 px-2 py-0.5 rounded">FEA-2026-000042</span>
                      </div>

                      <h3 className="mt-4 text-[22px] font-semibold leading-tight tracking-tight">Phase II Oncology Feasibility</h3>
                      <p className="mt-1 text-[13px] text-violet-100/85">PharmaCorp Ltd · Oncology · 4 PIs</p>

                      {/* Patient pool meter */}
                      <div className="mt-4 rounded-xl bg-white/[0.08] ring-1 ring-white/15 px-3.5 py-3 backdrop-blur">
                        <div className="flex items-center justify-between text-[11px]">
                          <span className="text-violet-100/80 uppercase tracking-wider">Patient pool</span>
                          <span className="text-white font-bold">
                            <Counter to={280} duration={1500} replayKey={`fea-pool-${frontCardIndex === 1 ? 'on' : 'idle'}`} />
                            <span className="text-violet-200/80 font-normal"> / 320 target</span>
                          </span>
                        </div>
                        <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
                          <div
                            key={`fea-bar-${frontCardIndex === 1 ? 'on' : 'idle'}`}
                            className="h-full rounded-full bg-gradient-to-r from-cyan-300 via-fuchsia-300 to-pink-300 transition-[width] duration-[1400ms] ease-out"
                            style={{ width: frontCardIndex === 1 ? '87.5%' : '0%' }}
                          />
                        </div>
                      </div>

                      <div className="mt-auto space-y-2">
                        <div className="flex items-center justify-between text-[12px] bg-white/[0.09] rounded-lg px-3 py-2 backdrop-blur ring-1 ring-white/15">
                          <span className="flex items-center gap-2 text-violet-50/90">
                            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-300" />
                            Chairman review
                          </span>
                          <span className="text-emerald-200 font-semibold">Approved</span>
                        </div>
                        <div className="flex items-center justify-between text-[12px] bg-white/[0.09] rounded-lg px-3 py-2 backdrop-blur ring-1 ring-white/15">
                          <span className="flex items-center gap-2 text-violet-50/90">
                            <Stethoscope className="h-3.5 w-3.5 text-cyan-200" />
                            Investigator feasibility
                          </span>
                          <span className="inline-flex items-center gap-1.5 text-cyan-200 font-semibold">
                            <span className="h-1.5 w-1.5 rounded-full bg-cyan-300 animate-pulse" />
                            Submitted
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-[12px] bg-white/[0.09] rounded-lg px-3 py-2 backdrop-blur ring-1 ring-white/15">
                          <span className="flex items-center gap-2 text-violet-50/90">
                            <Users className="h-3.5 w-3.5 text-pink-200" />
                            Sites engaged
                          </span>
                          <span className="text-white font-semibold">4 PIs · 6 centers</span>
                        </div>
                      </div>
                    </div>
                  </SwapCard>

                  {/* iter165 — Card 3: IRB. Emerald→teal with ethics meter
                      and audit-trail counter. */}
                  <SwapCard customClass="!bg-[linear-gradient(135deg,_#064e3b_0%,_#0f766e_45%,_#0e7490_100%)] text-white relative">
                    <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
                         style={{ backgroundImage:'linear-gradient(rgba(255,255,255,.4) 1px, transparent 1px)', backgroundSize:'100% 28px' }} />
                    <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-emerald-400/25 blur-3xl pointer-events-none" />
                    <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-emerald-300 via-teal-300 to-cyan-300" />

                    <div className="relative h-full p-7 flex flex-col">
                      <div className="flex items-center justify-between">
                        <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-white/18 backdrop-blur ring-1 ring-white/25 text-[10px] uppercase tracking-[0.18em] font-semibold text-emerald-50">
                          <ShieldCheck className="h-3 w-3" />
                          IRB Submission
                        </span>
                        <span className="text-[10px] font-mono text-emerald-50/90 bg-white/5 px-2 py-0.5 rounded">IRB-2026-000009</span>
                      </div>

                      <h3 className="mt-4 text-[22px] font-semibold leading-tight tracking-tight">Ethics Review · CAR-T Trial Protocol</h3>
                      <p className="mt-1 text-[13px] text-emerald-50/85">Institutional Review Board · Hematology</p>

                      <div className="mt-4 grid grid-cols-2 gap-2">
                        <div className="rounded-lg bg-white/[0.10] ring-1 ring-white/15 px-2.5 py-2 backdrop-blur">
                          <div className="text-[9px] uppercase tracking-wider text-emerald-100/80 flex items-center gap-1"><ScrollText className="h-2.5 w-2.5" /> Audit events</div>
                          <div className="text-lg font-bold text-white leading-tight">
                            <Counter to={63} duration={1400} replayKey={`irb-events-${frontCardIndex === 2 ? 'on' : 'idle'}`} />
                            <span className="text-xs text-emerald-200/80 font-normal"> logged</span>
                          </div>
                        </div>
                        <div className="rounded-lg bg-white/[0.10] ring-1 ring-white/15 px-2.5 py-2 backdrop-blur">
                          <div className="text-[9px] uppercase tracking-wider text-emerald-100/80 flex items-center gap-1"><Clock className="h-2.5 w-2.5" /> Review SLA</div>
                          <div className="text-lg font-bold text-white leading-tight">
                            <Counter to={4.2} decimals={1} duration={1300} replayKey={`irb-sla-${frontCardIndex === 2 ? 'on' : 'idle'}`} />
                            <span className="text-xs text-emerald-200/80 font-normal">d</span>
                          </div>
                        </div>
                      </div>

                      <div className="mt-auto space-y-2">
                        <div className="flex items-center justify-between text-[12px] bg-white/[0.10] rounded-lg px-3 py-2 backdrop-blur ring-1 ring-white/20">
                          <span className="flex items-center gap-2 text-emerald-50/90">
                            <span className="h-5 w-5 rounded-full bg-white/15 ring-1 ring-white/25 flex items-center justify-center text-[9px] font-bold text-white">IC</span>
                            IRB Chair
                          </span>
                          <span className="inline-flex items-center gap-1.5 text-emerald-100 font-semibold">
                            <CheckCircle2 className="h-3 w-3" />
                            Approved
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-[12px] bg-white/[0.10] rounded-lg px-3 py-2 backdrop-blur ring-1 ring-white/20">
                          <span className="flex items-center gap-2 text-emerald-50/90">
                            <span className="h-5 w-5 rounded-full bg-white/15 ring-1 ring-white/25 flex items-center justify-center text-[9px] font-bold text-white">EC</span>
                            Ethics committee
                          </span>
                          <span className="inline-flex items-center gap-1.5 text-amber-200 font-semibold">
                            <span className="h-1.5 w-1.5 rounded-full bg-amber-300 animate-pulse" />
                            Reviewing
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-[12px] bg-white/[0.10] rounded-lg px-3 py-2 backdrop-blur ring-1 ring-white/20">
                          <span className="flex items-center gap-2 text-emerald-50/90">
                            <Lock className="h-3.5 w-3.5 text-cyan-200" />
                            Tamper-proof seal
                          </span>
                          <span className="text-white font-semibold inline-flex items-center gap-1">
                            Verified <ChevronRight className="h-3 w-3" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </SwapCard>
                </CardSwap>
              </div>
              {/* iter164j — Floating Audit-ready + Automated reminders cards removed per user. */}
            </AnimatedContent>
          </div>
        </div>
        {/* iter164h — GradualBlur softly fades the bottom of the hero into
            the stats strip below. */}
        {/* iter167 — Explicit low zIndex (1) keeps the hero's bottom blur
            band UNDER the fixed StaggeredMenu panel (z-index 65). The
            component's default zIndex of 1000 was painting the blurry
            band on top of the open "I am a…" menu while scrolling. */}
        <GradualBlur
          position="bottom"
          height="5rem"
          strength={2}
          divCount={5}
          curve="ease-out"
          opacity={1}
          zIndex={1}
        />
      </section>

      {/* ====== STATS / TRUST STRIP — iter164i ====== */}
      {/* Moved from below the demo video → directly below the hero per user.
          Re-skinned from a flat slate row into a dramatic navy panel with
          per-stat icon, animated gradient halo behind each numeral, soft
          divider rules, a heading + caption, and a glowing "trusted-by"
          aesthetic that anchors the page social proof. */}
      <section
        className="relative overflow-hidden bg-[#0c1a30] text-white"
        data-testid="stats-strip"
      >
        {/* Decorative noise + radial accents */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none opacity-60"
          style={{
            backgroundImage:
              'radial-gradient(circle at 15% 30%, rgba(59,130,246,0.35) 0%, transparent 38%), radial-gradient(circle at 85% 70%, rgba(132,0,255,0.30) 0%, transparent 42%)',
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(to right, rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.04) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
            maskImage:
              'radial-gradient(ellipse at center, black 30%, transparent 70%)',
            WebkitMaskImage:
              'radial-gradient(ellipse at center, black 30%, transparent 70%)',
          }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
          <AnimatedContent>
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/8 ring-1 ring-white/15 text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-100">
                <Sparkles className="h-3 w-3" />
                Trusted by clinical research teams
              </div>
              <h2 className="mt-4 text-3xl sm:text-4xl font-semibold tracking-tight">
                A network that compounds, not just a tool.
              </h2>
              <p className="mt-3 text-base text-blue-100/70">
                Every metric below grows monthly as new centers, specialties,
                and sponsors plug into ClinicalFlow.
              </p>
            </div>
          </AnimatedContent>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/8 rounded-2xl overflow-hidden ring-1 ring-white/10">
            {stats.map((s, idx) => {
              const Icon = s.icon;
              return (
                <AnimatedContent key={s.label} delay={idx * 90}>
                  <div
                    className="group relative bg-[#0c1a30] p-7 lg:p-9 h-full flex flex-col justify-between min-h-[230px] hover:bg-[#10223e] transition-colors"
                    data-testid={`stat-tile-${idx}`}
                  >
                    {/* Decorative gradient halo behind the number */}
                    <div
                      aria-hidden
                      className={`absolute -top-6 left-6 w-32 h-32 rounded-full bg-gradient-to-br ${s.accent} blur-3xl opacity-25 group-hover:opacity-40 transition-opacity`}
                    />
                    {/* Top: icon */}
                    <div className="relative">
                      <div
                        className={`inline-flex items-center justify-center w-11 h-11 rounded-xl bg-gradient-to-br ${s.accent} text-[#0c1a30] shadow-lg shadow-black/30`}
                      >
                        <Icon className="h-5 w-5" strokeWidth={2.5} />
                      </div>
                    </div>
                    {/* Number + label */}
                    <div className="relative mt-6">
                      <div
                        className={`text-5xl lg:text-6xl font-bold tracking-tight tabular-nums bg-gradient-to-br ${s.accent} bg-clip-text text-transparent leading-none`}
                      >
                        <Counter to={s.value} suffix={s.suffix} duration={1800 + idx * 140} />
                      </div>
                      <div className="mt-3 text-sm font-semibold text-white">
                        {s.label}
                      </div>
                      <div className="mt-1 text-xs text-blue-100/55">
                        {s.caption}
                      </div>
                    </div>
                    {/* Subtle hover arrow */}
                    <div className="absolute top-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity">
                      <ArrowRight className="h-4 w-4 text-white/40" />
                    </div>
                  </div>
                </AnimatedContent>
              );
            })}
          </div>
        </div>
      </section>

      {/* ====== DEMO VIDEO ====== */}
      <DemoVideoSection />

      {/* ====== FEATURES ====== */}
      <section id="features" className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedContent>
            <div className="max-w-3xl mx-auto text-center">
              <div className="text-sm font-semibold text-[#1e3a5f] uppercase tracking-wider">
                What's inside
              </div>
              <h2 className="mt-3 text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">
                Everything your research workflow needs
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                Built from the ground up for clinical research centers,
                pharmaceutical sponsors, and investigative sites.
              </p>
            </div>
          </AnimatedContent>

          {/* iter164f — MagicBento (ReactBits) replaces the previous Spotlight
              feature grid. Six dark cards with a global cursor spotlight, per-
              card particles, magnetic pull, border-glow and click-ripple. The
              card content is sourced from `cards` so we stay in control of
              copy without forking the component itself. */}
          <AnimatedContent delay={100}>
            <div className="mt-14 flex justify-center">
              <MagicBento
                cards={featuresAsBentoCards}
                textAutoHide={true}
                enableStars={true}
                enableSpotlight={true}
                enableBorderGlow={true}
                enableTilt={false}
                clickEffect={true}
                enableMagnetism={true}
                particleCount={10}
                spotlightRadius={320}
                glowColor="200, 210, 225"
              />
            </div>
          </AnimatedContent>
        </div>
      </section>

      {/* ====== HOW IT WORKS ====== */}
      <section id="how-it-works" className="bg-slate-50 py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInOnScroll>
            <div className="max-w-3xl mx-auto text-center">
              <div className="text-sm font-semibold text-[#1e3a5f] uppercase tracking-wider">
                How it works
              </div>
              <h2 className="mt-3 text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">
                From request to contract in four clean steps
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                The right stakeholder, at the right stage, with the right
                context.
              </p>
            </div>
          </FadeInOnScroll>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:hidden">
            {steps.map((s, i) => (
              <FadeInOnScroll key={s.num} delay={i * 80}>
                <div className="relative h-full">
                  <div className="bg-white rounded-xl p-6 border border-slate-200 h-full">
                    <div className="text-sm font-mono font-semibold text-[#1e3a5f]/60">
                      {s.num}
                    </div>
                    <h3 className="mt-3 text-lg font-semibold text-slate-900">
                      {s.title}
                    </h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                      {s.desc}
                    </p>
                  </div>
                  {i < steps.length - 1 && (
                    <ArrowRight className="hidden lg:block absolute top-1/2 -right-5 -translate-y-1/2 h-5 w-5 text-slate-300" />
                  )}
                </div>
              </FadeInOnScroll>
            ))}
          </div>

          {/* iter165d — Desktop: ReactBits ScrollStack. Each step pins,
              scales, and stacks behind the next as the user scrolls,
              giving the four-step flow a cinematic feel. Cards are styled
              in ScrollStack.css to match the navy MagicBento + hero
              CardSwap palette so the page reads as one design system.
              iter165e — Resized per user: tighter spacing (itemDistance=50,
              itemStackDistance=10), smaller baseScale=0.75, and Lenis off
              to kill the trackpad vibration. */}
          <div className="hidden lg:block mt-12" data-testid="how-it-works-stack">
            <ScrollStack
              useWindowScroll
              useLenis={false}
              itemDistance={50}
              itemScale={0.03}
              itemStackDistance={10}
              stackPosition="22%"
              scaleEndPosition="12%"
              baseScale={0.75}
              rotationAmount={0}
              blurAmount={0}
            >
              {steps.map((s, i) => {
                const Icon = s.icon;
                return (
                  <ScrollStackItem key={s.num}>
                    <div className="relative grid grid-cols-12 gap-6 p-7 lg:p-9">
                      {/* Left: number + icon column */}
                      <div className="col-span-12 lg:col-span-4 flex lg:flex-col items-start gap-4">
                        <div className={`relative h-12 w-12 rounded-xl bg-gradient-to-br ${s.accent} p-[1.5px] shadow-lg shadow-black/30 shrink-0`}>
                          <div className="h-full w-full rounded-[10px] bg-[#06122a]/85 backdrop-blur flex items-center justify-center">
                            <Icon className="h-5 w-5 text-white" strokeWidth={1.75} />
                          </div>
                        </div>
                        <div className="leading-none">
                          <div className="text-[10px] uppercase tracking-[0.22em] font-mono text-cyan-200/70">
                            Step
                          </div>
                          <div className="mt-1.5 text-4xl lg:text-5xl font-bold tracking-tighter text-white">
                            {s.num}
                          </div>
                        </div>
                      </div>

                      {/* Right: copy + highlights */}
                      <div className="col-span-12 lg:col-span-8 flex flex-col">
                        <h3 className="text-2xl lg:text-3xl font-semibold text-white tracking-tight leading-tight">
                          {s.title}
                        </h3>
                        <p className="mt-2.5 text-sm lg:text-[15px] text-blue-100/85 leading-relaxed max-w-2xl">
                          {s.desc}
                        </p>

                        {/* Highlight chips */}
                        <div className="mt-4 flex flex-wrap gap-2">
                          {s.highlights.map((h) => (
                            <span
                              key={h}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/[0.07] ring-1 ring-white/15 backdrop-blur text-[11px] font-medium text-blue-50/95"
                            >
                              <CheckCircle2 className="h-3 w-3 text-emerald-300" />
                              {h}
                            </span>
                          ))}
                        </div>

                        {/* Bottom progress bar — fills proportional to step */}
                        <div className="mt-5">
                          <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-wider text-blue-200/70">
                            <span>Workflow progress</span>
                            <span>{Math.round(((i + 1) / steps.length) * 100)}%</span>
                          </div>
                          <div className="mt-1.5 h-1 rounded-full bg-white/10 overflow-hidden">
                            <div
                              className={`h-full rounded-full bg-gradient-to-r ${s.accent}`}
                              style={{ width: `${((i + 1) / steps.length) * 100}%` }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Corner step indicator chip */}
                      <div className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/[0.07] ring-1 ring-white/15 backdrop-blur text-[9px] uppercase tracking-[0.18em] font-semibold text-blue-100">
                        <span className="h-1 w-1 rounded-full bg-cyan-300 animate-pulse" />
                        {i === steps.length - 1 ? "Final stage" : `Stage ${i + 1} of ${steps.length}`}
                      </div>
                    </div>
                  </ScrollStackItem>
                );
              })}
            </ScrollStack>
          </div>
        </div>
      </section>

      {/* ====== WHY / VALUE PROPS ====== */}
      <section id="why" className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <FadeInOnScroll>
              <div>
              <div className="text-sm font-semibold text-[#1e3a5f] uppercase tracking-wider">
                Why ClinicalFlow
              </div>
              <h2 className="mt-3 text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">
                Purpose-built for clinical research, not retrofitted
              </h2>
              <p className="mt-4 text-lg text-slate-600 leading-relaxed">
                Generic project trackers don't understand feasibility stages,
                sponsor agreements, or regulatory audit requirements.
                ClinicalFlow does — out of the box.
              </p>

              <ul className="mt-8 space-y-4">
                {[
                  {
                    icon: Lock,
                    title: "Compliance-ready",
                    desc: "Immutable audit log captures every state change, document upload, signature, comment, and assignment.",
                  },
                  {
                    icon: Users,
                    title: "Built for multi-party collaboration",
                    desc: "Nine built-in roles map cleanly to how real research centers operate — no custom role mapping required.",
                  },
                  {
                    icon: Clock,
                    title: "Nothing falls through the cracks",
                    desc: "Stage-based reminder policies keep reviews moving even when your team is juggling twenty active studies.",
                  },
                ].map((item) => {
                  const I = item.icon;
                  return (
                    <li key={item.title} className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#1e3a5f]/5 flex items-center justify-center">
                        <I className="h-5 w-5 text-[#1e3a5f]" />
                      </div>
                      <div>
                        <div className="font-semibold text-slate-900">
                          {item.title}
                        </div>
                        <div className="mt-1 text-sm text-slate-600 leading-relaxed">
                          {item.desc}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
            </FadeInOnScroll>

            {/* Right: visual / screenshot placeholder */}
            <FadeInOnScroll delay={150}>
              <div className="relative">
              {/* iter164h — Folder decoration removed per user request. */}
              <div className="rounded-2xl overflow-hidden shadow-xl ring-1 ring-slate-900/10 bg-gradient-to-br from-[#1e3a5f] to-[#0f1e34] p-8 text-white">
                <div className="text-xs font-mono text-blue-200/80 mb-2">
                  Request #FEA-2026-000042
                </div>
                <div className="text-xl font-semibold">
                  Phase II Oncology Feasibility
                </div>
                <div className="mt-1 text-sm text-blue-100">
                  PharmaCorp Ltd · Oncology
                </div>

                <div className="mt-6 space-y-3">
                  {[
                    { label: "Chairman review", status: "Approved" },
                    { label: "Investigator feasibility", status: "Submitted" },
                    { label: "Director signature", status: "Pending" },
                  ].map((row) => (
                    <div
                      key={row.label}
                      className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2.5 backdrop-blur-sm border border-white/10"
                    >
                      <span className="text-sm">{row.label}</span>
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded ${
                          row.status === "Approved"
                            ? "bg-emerald-400/20 text-emerald-100"
                            : row.status === "Submitted"
                            ? "bg-blue-400/20 text-blue-100"
                            : "bg-amber-400/20 text-amber-100"
                        }`}
                      >
                        {row.status}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="mt-6 pt-6 border-t border-white/10 flex items-center justify-between text-xs text-blue-200">
                  <span>Audit trail · 47 events</span>
                  <span>Last activity · 2m ago</span>
                </div>
              </div>
            </div>
            </FadeInOnScroll>
          </div>
        </div>
      </section>

      {/* ====== FAQ (iter163g — paired with the FAQPage JSON-LD in index.html for AEO / featured-snippet pickup)
           iter164j — Section redesigned: gradient bg, headline pill, lead
           subhead with brand promise, glassy card list with hover lift, FAQ
           icon, and a CTA card at the bottom for missing-question cases. */}
      <section
        id="faq"
        className="relative py-24 lg:py-32 overflow-hidden bg-gradient-to-b from-slate-50 via-white to-slate-50"
        aria-labelledby="faq-heading"
      >
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none opacity-50"
          style={{
            backgroundImage:
              'radial-gradient(circle at 10% 10%, rgba(59,130,246,0.10) 0%, transparent 35%), radial-gradient(circle at 90% 90%, rgba(132,0,255,0.07) 0%, transparent 38%)',
          }}
        />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInOnScroll>
            <div className="text-center max-w-2xl mx-auto mb-14">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 ring-1 ring-blue-100 text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-800">
                <Sparkles className="h-3 w-3" />
                Frequently asked questions
              </div>
              <h2 id="faq-heading" className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold text-slate-900 tracking-tight leading-[1.1]">
                Everything you need to know about{" "}
                <span className="bg-gradient-to-br from-[#1e3a5f] via-blue-600 to-violet-600 bg-clip-text text-transparent">ClinicalFlow</span>
              </h2>
              <p className="mt-5 text-base sm:text-lg text-slate-600">
                Direct answers from our product team. Still have something on your mind?
                <a href="mailto:Contact@aretion.org" className="ml-1 text-blue-700 font-medium hover:underline">Email us</a>.
              </p>
            </div>
          </FadeInOnScroll>

          <div className="space-y-3">
            {[
              {
                q: "What is ClinicalFlow?",
                a: (
                  <>
                    ClinicalFlow is the <span className="font-semibold text-slate-900">operating system for clinical research operations</span> — a web-based platform where hospitals, sponsors and CROs run their entire request lifecycle in one place.
                    <br /><br />
                    Replace endless email chains, scattered spreadsheets and version-mismatched PDFs with <span className="font-semibold text-blue-700">role-based approvals, granular e-signatures and a tamper-proof audit trail</span> — built for the speed and rigor modern trials demand.
                  </>
                ),
              },
              {
                q: "What workflows does ClinicalFlow support?",
                a: (
                  <>
                    Three production-ready workflows ship out of the box, each with full multi-stage review and per-document e-signatures:
                    <br /><br />
                    <span className="font-semibold text-slate-900">· Feasibility Assessment (FEA)</span> — fast-track site &amp; PI feasibility with sponsor-visibility controls.
                    <br />
                    <span className="font-semibold text-slate-900">· Clinical Trial Agreement (CTA)</span> — Director → Executive Director → Chairman review with ordered, role-aware signatures.
                    <br />
                    <span className="font-semibold text-slate-900">· Financial Transaction (FT)</span> — budget and payment approval through the same governed pipeline.
                  </>
                ),
              },
              {
                q: "Is ClinicalFlow audit-ready for 21 CFR Part 11 / GxP?",
                a: (
                  <>
                    <span className="font-semibold text-slate-900">Yes — audit-ready by design.</span>
                    <br /><br />
                    Every action — approval, rejection, comment, signature, role change, document upload — is captured in an <span className="font-semibold text-blue-700">immutable, timestamped audit trail</span>. Each signed document carries a QR code that resolves to a public verification page proving signature timestamp, signatory identity and document hash. Inspections become a non-event.
                  </>
                ),
              },
              {
                q: "Who can sign documents in ClinicalFlow?",
                a: (
                  <>
                    Signatories are <span className="font-semibold text-slate-900">configured per document</span> by the Chairman during the <em>Start Review &amp; E-Signature Process</em> step — so every contract reflects its own governance.
                    <br /><br />
                    Supported roles: Investigator, Principal Investigator, Reviewer, Director, Executive Director, Financial Officer and Chairman. Signatures are collected in the <span className="font-semibold text-blue-700">exact order you configure</span>, with reminders firing automatically.
                  </>
                ),
              },
              {
                q: "Can sponsors and company representatives see the full workflow?",
                a: (
                  <>
                    Sponsors and Company Representatives get a <span className="font-semibold text-slate-900">privacy-masked view</span> tuned for transparency without exposing internal identities.
                    <br /><br />
                    They see assignment counts (e.g. <span className="font-mono text-blue-700">&ldquo;1 Reviewer assigned&rdquo;</span>) and approval markers in real time — but exact assignee names are revealed <span className="font-semibold text-blue-700">only after that assignee has approved</span> their part of the workflow. The result: external partners stay informed, your reviewers stay protected.
                  </>
                ),
              },
            ].map((item, i) => (
              <FadeInOnScroll key={i} delay={i * 60}>
                <details
                  className="group relative rounded-2xl border border-slate-200/80 bg-white/80 backdrop-blur px-6 py-5 hover:border-blue-300 hover:shadow-lg hover:shadow-blue-100/40 open:bg-white open:border-blue-300 open:shadow-lg open:shadow-blue-100/40 transition-all duration-200"
                  data-testid={`faq-item-${i}`}
                >
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-left">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-700 font-mono text-[12px] font-semibold ring-1 ring-blue-100 group-open:bg-blue-600 group-open:text-white group-open:ring-blue-600 transition-colors">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <h3 className="text-base sm:text-lg font-semibold text-slate-900 group-hover:text-blue-900 transition-colors">
                        {item.q}
                      </h3>
                    </div>
                    <span className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-slate-300 text-slate-500 transition-all group-open:rotate-45 group-open:border-blue-600 group-open:text-blue-600">
                      <span className="block h-px w-3.5 bg-current" />
                      <span className="absolute block h-3.5 w-px bg-current" />
                    </span>
                  </summary>
                  <p className="mt-4 pl-11 text-slate-600 leading-relaxed text-sm sm:text-base">
                    {item.a}
                  </p>
                </details>
              </FadeInOnScroll>
            ))}
          </div>

          {/* Bottom CTA card for unanswered questions */}
          <FadeInOnScroll delay={400}>
            <div className="mt-10 rounded-2xl border border-slate-200 bg-gradient-to-br from-[#1e3a5f] to-[#0f1e34] text-white p-8 flex flex-col sm:flex-row items-center gap-6 justify-between shadow-xl">
              <div className="text-center sm:text-left">
                <h3 className="text-xl font-semibold tracking-tight">Still have questions?</h3>
                <p className="mt-1 text-blue-100/85 text-sm">Our product team is one email away. We typically respond within 24 hours.</p>
              </div>
              <a href="mailto:Contact@aretion.org" className="shrink-0">
                <Button className="bg-white text-[#1e3a5f] hover:bg-blue-50 font-semibold h-11 px-5">
                  Email our team
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </div>
          </FadeInOnScroll>
        </div>
      </section>

      {/* ====== FINAL CTA ====== */}
      <section className="relative overflow-hidden bg-[#1e3a5f] text-white">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute -top-20 -left-20 w-[400px] h-[400px] bg-blue-400 rounded-full blur-3xl" />
          <div className="absolute -bottom-20 -right-20 w-[400px] h-[400px] bg-indigo-400 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
            Ready to get started?
          </h2>
          <p className="mt-5 text-lg text-blue-100 max-w-2xl mx-auto">
            Sign in to access your ClinicalFlow workspace — manage feasibility
            assessments, clinical trial agreements, and every stakeholder in
            between.
          </p>
          <div className="mt-10 flex justify-center">
            <Magnet padding={80} magnetStrength={5}>
              <Link to="/login">
                <Button
                  size="lg"
                  className="bg-white text-[#1e3a5f] hover:bg-slate-100 shadow-lg px-8 h-12 text-base font-semibold"
                  data-testid="cta-demo-btn"
                >
                  Sign in
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </Magnet>
          </div>
        </div>
      </section>

      {/* ====== FOOTER — iter164j: full footer with OFFICES (London + Gulf) + contact. ====== */}
      <footer className="bg-slate-900 text-slate-300 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {/* Brand column */}
            <div>
              <div className="flex items-center gap-2.5">
                <img src="/favicon-192.png" alt="ClinicalFlow" className="h-8 w-8 rounded-md" />
                <span className="font-semibold text-white text-lg">ClinicalFlow</span>
              </div>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed max-w-xs">
                Clinical research request automation for hospitals, sponsors and CROs.
              </p>
              <div className="mt-6 space-y-2 text-sm">
                <a href="mailto:Contact@aretion.org" className="flex items-center gap-2 hover:text-white transition-colors">
                  <Bell className="h-3.5 w-3.5 text-blue-400" />
                  Contact@aretion.org
                </a>
                <a href="tel:+966115256458" className="flex items-center gap-2 hover:text-white transition-colors">
                  <Clock className="h-3.5 w-3.5 text-blue-400" />
                  +966 11 525 6458
                </a>
              </div>
            </div>

            {/* OFFICES — London */}
            <div data-testid="footer-office-london">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-300">Offices · London</p>
              <address className="mt-4 not-italic text-sm text-slate-300 leading-relaxed">
                Shelton Street<br />
                Covent Garden<br />
                London, WC2H 9JQ<br />
                <span className="text-slate-400">United Kingdom</span>
              </address>
            </div>

            {/* OFFICES — Gulf */}
            <div data-testid="footer-office-gulf">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-300">Offices · Gulf</p>
              <address className="mt-4 not-italic text-sm text-slate-300 leading-relaxed">
                Innovation Boulevard, Al Aqeeq<br />
                King Abdullah Financial District<br />
                Building 7229, 13519<br />
                <span className="text-slate-400">Saudi Arabia</span>
              </address>
            </div>
          </div>

          <div className="mt-12 pt-6 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
            <span>&copy; {new Date().getFullYear()} ClinicalFlow · Clinical Research Management System</span>
            <div className="flex items-center gap-5">
              <Link to="/login" className="hover:text-white transition-colors">Sign in</Link>
              <a href="#features" className="hover:text-white transition-colors">Features</a>
              <a href="#faq" className="hover:text-white transition-colors">FAQ</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
